import { useMerchant } from "@/hooks/useMerchant";
import { useTRPC } from "@/trpc/client";
import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { PayoutBatchType } from "../types/payouts.type";

export type PayoutBatchUploadStatus =
  | { status: "idle" }
  | { status: "cancelled" }
  | { status: "uploading" }
  | { status: "processing" }
  | {
      status: "success";
      batchId: string;
      totalCount: number;
      totalAmount: number;
      errors?: Array<{
        index: number;
        requestLine: string;
        errorMessage: string;
      }>;
    }
  | {
      status: "failed";
      error: string;
      errors?: Array<{
        index: number;
        requestLine: string;
        errorMessage: string;
      }>;
    };

export function useUploadDispersionFile() {
  const trpc = useTRPC();
  const { merchant } = useMerchant();
  const queryClient = useQueryClient();

  const [uploadStatus, setUploadStatus] = useState<PayoutBatchUploadStatus>({
    status: "idle",
  });
  const [uploadReference, setUploadReference] = useState<string | null>(null);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getPresignedUrl = useMutation(
    trpc.payoutBatches.getPresignedUrl.mutationOptions()
  );

  const batchQuery = useQuery(
    trpc.payoutBatches.get.queryOptions(
      { rentalsMerchantId: merchant?.id || "", id: uploadReference || "" },
      {
        enabled: !!uploadReference && !!merchant?.id,
        refetchInterval: false,
        refetchOnWindowFocus: false,
      }
    )
  );

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const startPolling = (reference: string) => {
    setUploadReference(reference);
    setUploadStatus({ status: "processing" });

    if (!batchQuery.refetch) {
      console.warn("batchQuery.refetch is not ready yet");
      return;
    }

    stopPolling();
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await batchQuery.refetch();

        if (result.data) {
          if (
            result.data.status === "cancelled" ||
            result.data.status === "failed"
          ) {
            stopPolling();
            setUploadStatus({
              status: "failed",
              error: result.data.batchError || "Error al procesar el archivo",
              errors: result.data.errors || [],
            });
          } else if (
            result.data.status === "pending" ||
            result.data.status === "approved"
          ) {
            stopPolling();
            setUploadStatus({
              status: "success",
              batchId: result.data.id,
              totalCount: result.data.totalCount,
              totalAmount: result.data.totalAmount,
              errors: result.data.errors,
            });
            queryClient.invalidateQueries({
              queryKey: trpc.payoutBatches.list.queryKey(),
            });
          }
        }
      } catch (err) {
        console.error("Polling failed:", err);
      }
    }, 2000);
  };

  const uploadFile = async (
    file: File,
    options: { type: PayoutBatchType; exemptionDeclared: boolean }
  ) => {
    if (!merchant?.id) throw new Error("No merchant selected");

    setUploadStatus({ status: "uploading" });

    try {
      const { uploadUrl, reference } = await getPresignedUrl.mutateAsync({
        rentalsMerchantId: merchant.id,
        type: options.type,
      });

      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed (${response.status}): ${errorText}`);
      }

      startPolling(reference);
    } catch (error) {
      setUploadStatus({
        status: "failed",
        error:
          error instanceof Error ? error.message : "Error al subir el archivo",
      });
      throw error;
    }
  };

  const resetUpload = () => {
    stopPolling();
    setUploadReference(null);
    setUploadStatus({ status: "idle" });
  };

  const isUploading =
    uploadStatus.status === "uploading" || uploadStatus.status === "processing";

  return {
    uploadFile,
    uploadStatus,
    resetUpload,
    isUploading,
  };
}
